import { Transition } from '@headlessui/react';
import { useRef, useState } from 'react';
import { useFloating, offset, shift, arrow } from '@floating-ui/react-dom';
import { Placement } from '@floating-ui/core';
import { Options } from '@floating-ui/core/src/middleware/offset';
import { MdClose } from 'react-icons/md';
import classNames from 'classnames';

type ArrowPositionType = 'bottom' | 'left' | 'right' | 'top';

type Props = React.DetailedHTMLProps<
  React.InputHTMLAttributes<HTMLButtonElement> & {
    label: string | React.ReactNode;
    placement: Placement;
    offset?: Options;
    opened?: boolean;
    disabled?: boolean;
    children?: React.ReactNode;
    arrowPosition?: ArrowPositionType;
    closeTooltip?: () => void;
  },
  HTMLButtonElement
>;

export default function Tooltip({
  label,
  children,
  placement,
  offset: offsetPosition,
  arrowPosition = 'left',
  className,
  opened,
  closeTooltip,
  disabled,
}: Props) {
  const [open, setOpen] = useState(opened);
  const arrowRef = useRef(null);

  const { x, y, reference, floating, strategy, refs } = useFloating({
    placement: placement,
    middleware: [shift(), offset(offsetPosition), arrow({ element: arrowRef })],
  });

  const showTooltip = () => !disabled && setOpen(true);
  const disableTooltip = () => !disabled && setOpen(false);

  return (
    <>
      <button
        type="button"
        ref={reference}
        onMouseOver={showTooltip}
        onMouseOut={disableTooltip}
        className={classNames('font-bold block w-full', {
          'cursor-not-allowed text-gray-dark500': disabled,
        })}
      >
        <div
          className={classNames('flex items-center justify-center', {
            'opacity-30': disabled,
          })}
        >
          {children}
        </div>
      </button>

      {(opened || open) && (
        <div
          ref={floating}
          className={'z-10'}
          style={{
            position: strategy,
            left: x ?? 0,
            top: y ?? 0,
          }}
        >
          <div
            className="relative"
            style={{
              margin: '10px',
            }}
          >
            <div
              ref={arrowRef}
              className={classNames(
                'w-2 h-2 bg-gray-dark550 rotate-45',
                y && {
                  'top-50': y < 0,
                  'bottom-50': y > 0,
                },
                x && {
                  'left-50': x < 0,
                  'right-50': x > 0,
                }
              )}
              style={{
                position: strategy,
                [arrowPosition]: -4.5,
              }}
            />

            <Transition
              show={opened || open}
              enter="transition ease-out duration-100"
              enterFrom="transform opacity-0 scale-95"
              enterTo="transform opacity-100 scale-100"
              leave="transition ease-in duration-75"
              leaveFrom="transform opacity-100 scale-100"
              leaveTo="transform opacity-0 scale-95"
              beforeEnter={() => refs.reference.current}
              afterLeave={() => null}
              className={classNames(
                'bg-gray-dark550 text-gray-dark400 py-3 px-6 rounded-md text-xs relative',
                className
              )}
            >
              {opened && (
                <span
                  onClick={closeTooltip || disableTooltip}
                  className={'flex row justify-end cursor-pointer absolute'}
                  style={{ top: '5px', right: '5px' }}
                >
                  <MdClose size={15} />
                </span>
              )}
              {label}
            </Transition>
          </div>
        </div>
      )}
    </>
  );
}
